import { DollarSign } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { formatCurrency } from '@/lib/utils/format-business-data'
import type { PriceCalculationResult } from './price-calculator-types'

interface PriceCalculatorSummaryProps {
  result: PriceCalculationResult
}

export function PriceCalculatorSummary({ result }: PriceCalculatorSummaryProps) {
  if (result.nightCount === 0) return null

  return (
    <div className="space-y-3">
      <Separator />
      <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
        {/* Total */}
        <div>
          <span className="text-xs text-muted-foreground">Total Cost</span>
          <div className="text-2xl font-bold">{formatCurrency(result.totalCost)}</div>
        </div>

        {/* Stats */}
        <div className="flex gap-4 text-sm">
          <Stat label="Avg/Night" value={formatCurrency(result.averageNightlyRate)} />
          <Stat label="Nights" value={String(result.nightCount)} />
          <Stat label="Lowest" value={formatCurrency(result.lowestRate)} />
          <Stat label="Highest" value={formatCurrency(result.highestRate)} />
        </div>
      </div>

      {/* Tier breakdown */}
      <div className="flex flex-wrap items-center gap-2">
        {result.lowNights > 0 && (
          <Badge variant="outline" className="gap-1 text-green-600 dark:text-green-400 border-green-300 dark:border-green-700">
            <span className="w-2 h-2 rounded-full bg-green-500" />
            {result.lowNights} low
          </Badge>
        )}
        {result.standardNights > 0 && (
          <Badge variant="outline" className="gap-1 text-amber-600 dark:text-amber-400 border-amber-300 dark:border-amber-700">
            <span className="w-2 h-2 rounded-full bg-amber-500" />
            {result.standardNights} standard
          </Badge>
        )}
        {result.peakNights > 0 && (
          <Badge variant="outline" className="gap-1 text-red-600 dark:text-red-400 border-red-300 dark:border-red-700">
            <span className="w-2 h-2 rounded-full bg-red-500" />
            {result.peakNights} peak
          </Badge>
        )}

        {result.savingsVsPeak > 0 && (
          <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400 ml-auto">
            <DollarSign className="w-3.5 h-3.5" />
            You save {formatCurrency(result.savingsVsPeak)} vs. all-peak
          </span>
        )}
      </div>
    </div>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="text-xs text-muted-foreground">{label}</span>
      <div className="font-medium">{value}</div>
    </div>
  )
}
